import { Node } from "reactflow";
import utils from "core/utils";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import { getComponentId, findReferencedComponents } from "./component.utils";
import { LAYOUT_CONFIG } from "./flow.constants";

// ============================================================================
// CROSS-FLOW CONNECTION UTILITIES
// ============================================================================

/**
 * Finds cross-flow connections for a given component
 */
export const findCrossFlowConnections = (
    component: any,
    componentToFlowMap: Map<string, string>,
    currentFlowId: string
): string[] => {
    const connectedFlows: string[] = [];
    const componentType = component.type;

    // Check different types of connections based on component type
    const checkConnection = (targetId: string) => {
        if (targetId && componentToFlowMap.has(targetId)) {
            const targetFlowId = componentToFlowMap.get(targetId);
            if (targetFlowId && targetFlowId !== currentFlowId) {
                connectedFlows.push(targetFlowId);
            }
        }
    };

    // Handle different component types and their connection properties
    if (
        (componentType === utils.ENTITIES.SOURCE || componentType === utils.ENTITIES.EXTERNALSOURCE) &&
        component.outputStream
    ) {
        checkConnection(component.outputStream);
    }

    if (componentType === utils.ENTITIES.TARGET && component.inputStream) {
        checkConnection(component.inputStream);
    }

    if (componentType === utils.ENTITIES.CQ) {
        if (component.inputs && Array.isArray(component.inputs)) {
            component.inputs.forEach(checkConnection);
        }
        if (component.output) {
            checkConnection(component.output);
        }
    }

    if (componentType === utils.ENTITIES.WINDOW && component.stream) {
        checkConnection(component.stream);
    }

    if (componentType === utils.ENTITIES.ROUTER) {
        if (component.inStream) {
            checkConnection(component.inStream);
        }
        if (component.forwardingRules && Array.isArray(component.forwardingRules)) {
            component.forwardingRules.forEach((rule: any) => {
                if (rule.outStream) {
                    checkConnection(rule.outStream);
                }
            });
        }
    }

    if (componentType === utils.ENTITIES.OPENPROCESSOR) {
        if (component.from) {
            const inputs = Array.isArray(component.from) ? component.from : [component.from];
            inputs.forEach((inputId: string) => checkConnection(inputId));
        }
        if (component.output) {
            checkConnection(component.output);
        }
    }

    if (componentType === utils.ENTITIES.SENTINEL) {
        if (component.inputStream) {
            checkConnection(component.inputStream);
        }
        if (component.outputStream) {
            checkConnection(component.outputStream);
        }
    }

    return [...new Set(connectedFlows)]; // Remove duplicates
};

// ============================================================================
// FLOW MAPPING UTILITIES
// ============================================================================

/**
 * Build component-to-flow mapping from FLOW nodes
 */
export const buildComponentToFlowMapping = (nodes: Node[]): Map<string, string> => {
    const componentToFlowMap = new Map<string, string>();

    nodes.forEach(node => {
        if (node.data?.componentType === utils.ENTITIES.FLOW) {
            let objects = null;

            // Try to find objects property in different locations
            if (node.data?.metaObject?.objects) {
                objects = node.data.metaObject.objects;
            } else if (node.data?.metaObject) {
                const metaObj = node.data.metaObject;
                if (metaObj.objects) {
                    objects = metaObj.objects;
                }
            }

            if (objects && typeof objects === "object" && objects !== null) {
                Object.entries(objects).forEach(([, componentList]: [string, any]) => {
                    if (Array.isArray(componentList)) {
                        componentList.forEach((componentId: string) => {
                            componentToFlowMap.set(componentId, node.id);
                        });
                    }
                });
            }
        }
    });

    return componentToFlowMap;
};

/**
 * Builds a lookup structure mapping every component ID to the flow it belongs to.
 *
 * How it works:
 * 1. Iterates over `flowComponents` (pre-filtered FLOW-type components supplied by the caller) and
 *    for each flow walks `flow.objects` (a map of component-type → component-id[]), registering
 *    every component ID in both `componentsInFlows` and `componentToFlowMap` pointing to that
 *    flow's ID.
 * 2. Any component in `appComponents` that was NOT claimed by a flow (i.e. app-level components
 *    with no parent flow) is registered under `appFQN` so they are still reachable in the map.
 *
 * Returns:
 *  - `componentsInFlows` – a Set of every component ID seen across all flows (and app-level).
 *  - `componentToFlowMap` – a Map<componentId, flowId | appFQN> used by cross-flow utilities to
 *    determine which flow a given component lives in.
 */
export const createComponentToFlowMapping = (
    appComponents: any[],
    flowComponents: any[],
    appFQN: string
): {
    componentsInFlows: Set<string>;
    componentToFlowMap: Map<string, string>;
} => {
    const componentsInFlows = new Set<string>();
    const componentToFlowMap = new Map<string, string>();

    flowComponents.forEach((flow: any) => {
        if (flow.objects) {
            Object.values(flow.objects).forEach((componentList: any) => {
                if (Array.isArray(componentList)) {
                    componentList.forEach((componentId: string) => {
                        componentsInFlows.add(componentId);
                        componentToFlowMap.set(componentId, flow.id);
                    });
                }
            });
        }
    });
    // Components in appComponents but not in flows
    appComponents.forEach((component: any) => {
        if (!componentsInFlows.has(component.id)) {
            componentsInFlows.add(component.id);
            componentToFlowMap.set(component.id, appFQN);
        }
    });

    return { componentsInFlows, componentToFlowMap };
};

// ============================================================================
// CROSS-FLOW NODE CREATION
// ============================================================================

/**
 * Builds placeholder FLOW nodes for every external flow that is connected to the currently
 * viewed flow, so the canvas can render cross-flow edges without displaying the internals of
 * those other flows.
 *
 * How it works:
 * 1. **Outgoing connections** — iterates components inside `currentFlowComponents` and calls
 *    `findCrossFlowConnections` to collect IDs of flows they reference.
 * 2. **Incoming connections** — iterates all other app components and uses
 *    `findReferencedComponents` to detect any that point at something inside `selectedFlow`;
 *    their owning flow is then added to the connected-flows set.
 *    (Only direct neighbours are found — transitive flows are not traversed.)
 * 3. **Node creation** — for each connected flow ID, looks up the flow metadata and creates a
 *    FLOW-type `Node` with placeholder `{x:0, y:0}` positions (the caller's layout algorithm
 *    assigns real positions). If the flow ID has no matching flow object it is treated as an
 *    app-level node (`isAppFlow: true`).
 */
export const createCrossFlowNodes = async (
    appComponents: any[],
    flows: any[],
    selectedFlow: any,
    componentsInSelectedFlow: Set<string>,
    componentToFlowMap: Map<string, string>,
    xOffset: number,
    yOffset: number,
    horizontalSpacing: number,
    verticalSpacing: number,
    componentsInRow: number,
    maxComponentsPerRow: number
): Promise<Node[]> => {
    const crossFlowNodes: Node[] = [];
    const connectedFlows = new Set<string>();

    // Find all components in the current flow that have connections to other flows
    const currentFlowComponents = appComponents.filter((component: any) => componentsInSelectedFlow.has(component.id));

    // Go through all the components inside the current flow
    // Find the cross flow connections and add them to `connectedFlows` set (Outgoing connections)
    for (const component of currentFlowComponents) {
        const crossFlowConnections = findCrossFlowConnections(component, componentToFlowMap, selectedFlow.id);
        crossFlowConnections.forEach(flowId => connectedFlows.add(flowId));
    }

    // Go through all the components outside the flow and find incoming connections
    // Also check for incoming cross-flow connections (components in other flows that reference current flow components)
    // NOTE: This only finds DIRECT connections. If Flow A -> Flow B -> Flow C, only Flow B will appear in Flow A's view
    for (const component of appComponents) {
        // Skip components in the current flow
        if (componentsInSelectedFlow.has(component.id)) continue;

        // Check if this component references any components in the current flow
        const referencedComponents = findReferencedComponents(component);
        for (const referencedId of referencedComponents) {
            if (componentsInSelectedFlow.has(referencedId)) {
                // This component in another flow references something in the current flow
                const componentFlowId = componentToFlowMap.get(component.id);
                if (componentFlowId && componentFlowId !== selectedFlow.id) {
                    connectedFlows.add(componentFlowId);
                }
            }
        }
    }

    // Create flow nodes for each connected flow
    let currentXOffset = xOffset;
    let currentYOffset = yOffset;
    let currentComponentsInRow = componentsInRow;

    for (const flowId of connectedFlows) {
        let flow = flows.find(f => f.id === flowId);
        let componentId = "";
        const isAppFlow = !flow;

        if (isAppFlow) {
            // Create flow node for App level View
            const AppFlowNode = { id: flowId, name: MetaObjectConverter.getName(flowId) };
            flow = AppFlowNode;
            componentId = AppFlowNode.name;
        } else {
            componentId = getComponentId(flow, utils.ENTITIES.FLOW);
        }

        // Calculate position for the flow node
        if (currentComponentsInRow >= maxComponentsPerRow) {
            currentComponentsInRow = 0;
            currentYOffset += verticalSpacing;
            currentXOffset = LAYOUT_CONFIG.INITIAL_X_OFFSET;
        }

        const flowNode: Node = {
            id: flowId,
            type: utils.ENTITIES.FLOW,
            position: {
                x: currentXOffset,
                y: currentYOffset
            },
            data: {
                label: flow.name || MetaObjectConverter.getName(flow.id),
                type: utils.ENTITIES.FLOW,
                componentType: utils.ENTITIES.FLOW,
                name: flow.name || MetaObjectConverter.getName(flow.id),
                componentId: componentId,
                metaObjectId: flow.id,
                targets: [],
                metaObject: flow,
                isAppFlow: isAppFlow
            }
        };

        crossFlowNodes.push(flowNode);
        currentXOffset += horizontalSpacing;
        currentComponentsInRow++;
    }

    return crossFlowNodes;
};
